<template>
    <PageTitle title="Caisses" description="Gerez vos caisses et ventes en temps reel">
        <template #actions>
            <button v-if="activeCashRegister"
                v-permission="'cash-registers.close'"
                class="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors duration-200"
                @click="openCloseModal(activeCashRegister)">
                <i class="fas fa-xmark text-white text-sm"></i>
                Fermer la caisse
            </button>
        </template>
    </PageTitle>

    <div class="space-y-6">
        <!-- Etat actuel de la caisse -->
        <div class="bg-white rounded-xl border border-gray-200">
            <!-- Caisse ouverte -->
            <div v-if="activeCashRegister" class="p-5 space-y-5">
                <!-- En-tete : statut + CA -->
                <div class="flex items-center justify-between pb-5 border-b border-gray-100">
                    <div class="space-y-1">
                        <div class="flex items-center gap-2">
                            <span class="relative flex h-2.5 w-2.5">
                                <span
                                    class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                <span class="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                            </span>
                            <span class="text-sm font-semibold text-green-700">Caisse ouverte</span>
                        </div>
                        <p class="text-xs text-gray-500">{{ formatDate(activeCashRegister.start_date) }}</p>
                    </div>
                    <div class="text-right">
                        <p class="text-2xl font-bold text-gray-900">{{ formatCurrency(cashRegisterStats?.ca?.ttc || 0) }}</p>
                        <p class="text-[11px] text-gray-400 uppercase tracking-wider">CA Total</p>
                    </div>
                </div>

                <!-- Stats rapides -->
                <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div class="bg-gray-50 rounded-lg px-4 py-3 text-center">
                        <p class="text-base font-bold text-gray-900">{{ formatCurrency(activeCashRegister.start_balance) }}</p>
                        <p class="text-[11px] text-gray-400 uppercase tracking-wider mt-0.5">Fond initial</p>
                    </div>
                    <div class="bg-gray-50 rounded-lg px-4 py-3 text-center">
                        <p class="text-base font-bold text-gray-900">{{ cashRegisterStats?.sales?.total || 0 }}</p>
                        <p class="text-[11px] text-gray-400 uppercase tracking-wider mt-0.5">Ventes totales</p>
                    </div>
                    <div class="bg-gray-50 rounded-lg px-4 py-3 text-center">
                        <p class="text-base font-bold text-gray-900">{{ cashRegisterStats?.sales?.completed || 0 }}</p>
                        <p class="text-[11px] text-gray-400 uppercase tracking-wider mt-0.5">Completees</p>
                    </div>
                    <div class="bg-gray-50 rounded-lg px-4 py-3 text-center">
                        <p class="text-base font-bold text-gray-900">{{ cashRegisterStats?.sales?.pending || 0 }}</p>
                        <p class="text-[11px] text-gray-400 uppercase tracking-wider mt-0.5">En attente</p>
                    </div>
                </div>

                <!-- Actions de vente -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                        class="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl text-left hover:border-primary transition-colors duration-200 group"
                        @click="onMakeCashSale">
                        <div class="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                            <img src="@/assets/images/cash-register/sale-simple.png" alt="Vente Comptant"
                                class="w-full h-full object-cover" />
                        </div>
                        <div class="flex-1 min-w-0">
                            <h3 class="text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors">Vente Comptant</h3>
                            <p class="text-xs text-gray-400 mt-0.5">Paiement immediat</p>
                            <p class="text-sm font-bold text-gray-900 mt-1">
                                {{ formatCurrency(cashRegisterStats?.by_type?.single?.ca_ttc || 0) }}
                            </p>
                        </div>
                    </button>

                    <button
                        class="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl text-left hover:border-primary transition-colors duration-200 group"
                        @click="onMakeDeferredSale">
                        <div class="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                            <img src="@/assets/images/cash-register/sale-differed.png" alt="Vente Differee"
                                class="w-full h-full object-cover" />
                        </div>
                        <div class="flex-1 min-w-0">
                            <h3 class="text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors">Bons de commande</h3>
                            <p class="text-xs text-gray-400 mt-0.5">Paiement ulterieur</p>
                            <p class="text-sm font-bold text-gray-900 mt-1">
                                {{ formatCurrency(cashRegisterStats?.by_type?.deferred?.ca_ttc || 0) }}
                            </p>
                        </div>
                    </button>
                </div>
            </div>

            <!-- Aucune caisse ouverte -->
            <div v-else class="py-16 px-6 text-center">
                <div class="text-gray-300 mb-4">
                    <BaseIcon name="inbox" :size="40" />
                </div>
                <h3 class="text-base font-semibold text-gray-700 mb-1">Aucune caisse ouverte</h3>
                <p class="text-sm text-gray-400 mb-6">Ouvrez une caisse pour commencer vos ventes</p>
                <button
                    class="px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors duration-200"
                    @click="showOpenModal = true">
                    Ouvrir une caisse
                </button>
            </div>
        </div>

        <!-- Dernieres ventes -->
        <div v-if="activeCashRegister" class="bg-white rounded-xl border border-gray-200">
            <!-- Header -->
            <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <h3 class="text-sm font-semibold text-gray-700">Dernieres ventes</h3>
                <span class="text-xs text-gray-400">{{ lastSales.length }} ventes recentes</span>
            </div>

            <!-- Loading -->
            <div v-if="loadingSales" class="p-12 flex flex-col items-center justify-center gap-3">
                <div class="w-7 h-7 border-3 border-gray-200 border-t-primary rounded-full animate-spin"></div>
                <p class="text-sm text-gray-400">Chargement...</p>
            </div>

            <!-- Empty -->
            <div v-else-if="lastSales.length === 0" class="p-12 text-center text-sm text-gray-400">
                Aucune vente aujourd'hui
            </div>

            <!-- Sales list -->
            <div v-else class="divide-y divide-gray-100">
                <div v-for="sale in lastSales" :key="sale.id"
                    class="flex items-center gap-4 px-5 py-3 hover:bg-gray-50/80 transition-colors">
                    <!-- Info -->
                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2">
                            <span class="text-sm font-semibold text-gray-900">#{{ sale.invoice_number }}</span>
                            <span class="text-xs text-gray-400">{{ sale.user?.name }}</span>
                        </div>
                        <p class="text-xs text-gray-400 mt-0.5">{{ formatShortDate(sale.sale_date || sale.created_at) }}</p>
                    </div>

                    <!-- Amount -->
                    <div class="text-sm font-bold text-gray-900 whitespace-nowrap">
                        {{ formatCurrency(sale.total_incl_tax ?? sale.total) }}
                    </div>

                    <!-- Status -->
                    <div class="hidden sm:block">
                        <SaleStatusBadge :sale="sale" />
                    </div>

                    <!-- Payment method -->
                    <div class="hidden md:block text-xs text-gray-400 w-20 text-right">
                        {{ extractPaymentMethods(sale.payments) || 'Especes' }}
                    </div>

                    <!-- Actions -->
                    <div class="flex items-center gap-1">
                        <button
                            class="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            @click="viewSaleDetails(sale)" title="Voir details">
                            <BaseIcon name="eye" :size="15" />
                        </button>
                        <button
                            class="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            @click="printTicket(sale)" title="Imprimer ticket">
                            <BaseIcon name="printer" :size="15" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modales -->
    <OpenCashRegisterModal :show="showOpenModal" @close="showOpenModal = false" @success="onOpened" />
    <CloseCashRegisterModal :show="showCloseModal" :cash-register="selectedCashRegister" :statistics="cashRegisterStats"
        @close="closeCloseModal" @success="onClosed" />
</template>

<script>
import { ref, onMounted, defineExpose } from 'vue'
import { useRouter } from 'vue-router'
import CashRegisterService from '@/services/cashRegisterService'
import { salesService } from '@/services'
import OpenCashRegisterModal from '@/views/cashRegister/OpenCashRegisterModal.vue'
import CloseCashRegisterModal from '@/views/cashRegister/CloseCashRegisterModal.vue'
import PageTitle from '@/components/common/PageTitle.vue'
import BaseIcon from '@/components/base/BaseIcon.vue'
import SaleStatusBadge from '@/components/sales/SaleStatusBadge.vue'
import thermalPrinterService from '@/services/thermalPrinterService'
import { useToastStore } from '@/store/modules/toastStore'
export default {
    name: 'CashRegisterHome',
    components: {
        OpenCashRegisterModal,
        CloseCashRegisterModal,
        PageTitle,
        SaleStatusBadge,
        BaseIcon
    },
    emits: ['register-changed'],
    setup(props, { emit }) {
        const router = useRouter()
        const activeCashRegister = ref(null)
        const cashRegisterStats = ref(null)
        const loadingStats = ref(false)
        const showOpenModal = ref(false)
        const showCloseModal = ref(false)
        const selectedCashRegister = ref(null)
        const lastSales = ref([])
        const loadingSales = ref(false)
        const toastStore = useToastStore()

        const formatDate = (date) => {
            if (!date) return '-'
            return new Date(date).toLocaleString('fr-FR', {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
            })
        }

        const formatShortDate = (date) => {
            if (!date) return '-'
            return new Date(date).toLocaleString('fr-FR', {
                month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
            })
        }

        const formatCurrency = (amount) => {
            if (amount === null || amount === undefined) return '0 F'
            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'XOF',
                minimumFractionDigits: 0,
                currencyDisplay: 'symbol'
            }).format(amount).replace('XOF', 'F')
        }

        const viewSaleDetails = (sale) => {
            router.push({
                name: 'sale-details',
                params: { id: sale.id }
            })
        }

        const printTicket = async (sale) => {
            try {
                const saleResponse = await salesService.getSale(sale.id);
                await thermalPrinterService.printTicket(saleResponse.data.data, {
                    connectionType: 'usb',
                    shopInfo: JSON.parse(localStorage.getItem('company'))
                })
            } catch (error) {
                toastStore.addToast({
                    type: 'error',
                    message: error.message || 'Erreur lors de l\'impression du ticket',
                    duration: 4000
                })
            }
        }

        const fetchOpenedRegisterAndStats = async () => {
            loadingStats.value = true
            try {
                const opened = await CashRegisterService.getOpenedCashRegister()
                activeCashRegister.value = opened || null
                if (activeCashRegister.value) {
                    const resp = await CashRegisterService.getCashRegisterStats(activeCashRegister.value.id)
                    cashRegisterStats.value = resp?.data?.statistics || null
                    await fetchLastSales()
                } else {
                    cashRegisterStats.value = null
                    lastSales.value = []
                    CashRegisterService.clearOpenedCashRegister()
                }
            } finally {
                loadingStats.value = false
            }
        }

        const extractPaymentMethods = (sale) => {
            return sale?.payment_label ?? '';
        }

        const fetchLastSales = async () => {
            if (!activeCashRegister.value) return
            loadingSales.value = true
            try {
                const response = await salesService.getSales({
                    cash_register_id: activeCashRegister.value.id,
                    per_page: 8,
                    page: 1,
                    sort_by: 'sale_date',
                    sort_direction: 'desc'
                })
                if (response?.data?.data) {
                    lastSales.value = response.data.data
                } else if (response?.data?.sales) {
                    lastSales.value = response.data.sales.slice(0, 10)
                } else if (Array.isArray(response?.data)) {
                    lastSales.value = response.data.slice(0, 10)
                } else {
                    lastSales.value = []
                }
            } catch (e) {
                lastSales.value = []
            } finally {
                loadingSales.value = false
            }
        }

        const onMakeCashSale = () => router.push({ name: 'sale-simple' })
        const onMakeDeferredSale = () => router.push({ name: 'sale-differe' })

        const openCloseModal = (cashRegister) => {
            if (!cashRegister) return
            selectedCashRegister.value = cashRegister
            showCloseModal.value = true
        }

        const closeCloseModal = () => {
            selectedCashRegister.value = null
            showCloseModal.value = false
        }

        const onOpened = async () => {
            await fetchOpenedRegisterAndStats()
            emit('register-changed')
        }

        const onClosed = async () => {
            await fetchOpenedRegisterAndStats()
            emit('register-changed')
        }

        const CACHE_NAME = 'sale-buttons-v1'
        const EXPIRATION_TIME = 3 * 24 * 60 * 60 * 1000

        const saleImages = [
            require('@/assets/images/cash-register/sale-simple.png'),
            require('@/assets/images/cash-register/sale-differed.png')
       ]

        async function cacheSaleImages () {
            const cache = await caches.open(CACHE_NAME)
            for (const url of saleImages) {
                const cached = await cache.match(url)
                const savedTime = localStorage.getItem(url)

                if (!cached || !savedTime || Date.now() - savedTime > EXPIRATION_TIME) {
                    const response = await fetch(url)
                    await cache.put(url, response.clone())
                    localStorage.setItem(url, Date.now())
                }
            }
        }

        onMounted(() => {
            cacheSaleImages()
            fetchOpenedRegisterAndStats()
        })

        defineExpose({
            openCloseModal
        })

        return {
            // state
            activeCashRegister,
            cashRegisterStats,
            showOpenModal,
            showCloseModal,
            selectedCashRegister,
            lastSales,
            loadingSales,
            // methods
            formatDate,
            formatShortDate,
            formatCurrency,
            extractPaymentMethods,
            viewSaleDetails,
            printTicket,
            onMakeCashSale,
            onMakeDeferredSale,
            openCloseModal,
            closeCloseModal,
            onOpened,
            onClosed
        }
    }
}
</script>
